import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export const Notifications = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([
        api.get("/notifications/count"),
        api.get("/notifications"),
      ]);
      setUnread(c.data.unread || 0);
      setItems(l.data);
    } catch {}
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    refresh();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    refresh();
  };

  return (
    <div className="relative">
      <button
        data-testid="notifications-bell"
        onClick={() => { setOpen(!open); refresh(); }}
        className="relative flex h-10 w-10 items-center justify-center border border-border bg-white text-slate-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-count"
            className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center bg-[#FF3B30] px-1 text-[0.65rem] font-bold text-white"
          >
            {unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-testid="notifications-panel"
            className="absolute right-0 z-50 mt-2 max-h-[70vh] w-[min(22rem,calc(100vw-2rem))] overflow-y-auto border border-border bg-white shadow-xl"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
              <span className="overline text-[0.65rem] text-slate-500">Notifications</span>
              {unread > 0 && (
                <button data-testid="mark-all-read" onClick={markAll} className="flex items-center gap-1 text-xs font-semibold text-[#002FA7]">
                  <Check size={13} /> Mark all read
                </button>
              )}
            </div>
            {items.length === 0 ? (
              <p className="px-4 py-5 text-sm text-slate-500" data-testid="notifications-empty">
                Nothing yet. New ride requests and booking updates land here.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      data-testid={`notification-${n.id}`}
                      onClick={() => openItem(n)}
                      className={`w-full px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-[#F5F8FF]"}`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 bg-[#002FA7]" />}
                        <div className="min-w-0">
                          <div className="text-sm font-semibold leading-snug text-[#0F172A]">{n.title}</div>
                          {n.body && <div className="mt-0.5 text-xs text-slate-500">{n.body}</div>}
                          <div className="mt-1 text-[0.65rem] uppercase tracking-wider text-slate-400">
                            {(n.created_at || "").slice(0, 16).replace("T", " ")}
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};
